import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Container, Grid, Box, Typography, Button, Paper } from '@mui/material';
import Header from './components/Header';
import CurrencyCard from './components/CurrencyCard';
import CurrencySkeleton from './components/CurrencySkeleton';
import MarketOverview from './components/MarketOverview';
import ControlsBar from './components/ControlsBar';
import ConverterModal from './components/ConverterModal';
import { SearchOffRounded as SearchOffRoundedIcon } from '@mui/icons-material';
import { getTranslation, getCurrencyFullName, toPersianDigits } from './i18n';

const App = ({ toggleTheme, language = 'fa', toggleLanguage, t: customT }) => {
  const t = customT || ((key, params) => getTranslation(language, key, params));
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [time, setTime] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Currencies, 2: Gold
  const [converterOpen, setConverterOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const handleOpenConverter = (shortCode = 'USD') => {
    if (shortCode && typeof shortCode === 'string') {
      setSelectedCurrency(shortCode.toUpperCase());
    }
    setConverterOpen(true);
  };

  const fetchCurrencyData = useCallback((isManual = false) => {
    if (isManual) setIsRefreshing(true);

    const apiUrl = 'https://mr-alireza.ir/arzgo/main/api/Request.php';
    const formdata = new FormData();
    formdata.append("token", "VkO391ymfubNpvnd8Zu6");
    formdata.append("lang", language);

    fetch(apiUrl, {
      method: 'POST',
      body: formdata,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const cny = (data.cny || []).map((currency) => {
          const upperShort = currency.short ? currency.short.toUpperCase() : '';
          const rawSell = currency.sell ? currency.sell.toLocaleString() : '-';
          const rawBuy = currency.buy ? currency.buy.toLocaleString() : '-';
          return {
            short: upperShort,
            full: getCurrencyFullName(language, currency.short, currency.full),
            sell: language === 'fa' ? toPersianDigits(rawSell) : rawSell,
            buy: language === 'fa' ? toPersianDigits(rawBuy) : rawBuy,
            flag: currency.flag || '🌐',
            type: 'currency',
          };
        });
        const golds = (data.golds || []).map((currency) => {
          const upperShort = currency.short ? currency.short.toUpperCase() : '';
          const rawPrice = currency.price ? currency.price.toLocaleString() : '-';
          return {
            short: upperShort,
            full: getCurrencyFullName(language, currency.short, currency.name),
            sell: language === 'fa' ? toPersianDigits(rawPrice) : rawPrice,
            buy: '‌',
            flag: '🪙',
            type: 'gold',
          };
        });
        setCurrencies([...cny, ...golds]);
        const rawTime = data.time || new Date().toLocaleTimeString();
        setTime(language === 'fa' ? toPersianDigits(rawTime) : rawTime);
      })
      .catch((error) => {
        console.error('Error fetching currency data:', error);
      })
      .finally(() => {
        setLoading(false);
        setIsRefreshing(false);
      });
  }, [language]);

  useEffect(() => {
    fetchCurrencyData();
    const intervalId = setInterval(() => fetchCurrencyData(false), 60000);
    return () => clearInterval(intervalId);
  }, [fetchCurrencyData]);

  // Filtered currencies based on search & tab selection
  const filteredCurrencies = useMemo(() => {
    return currencies.filter((item) => {
      // Tab filter
      if (activeTab === 1 && item.type !== 'currency') return false;
      if (activeTab === 2 && item.type !== 'gold') return false;

      // Search query filter
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        (item.short && item.short.toLowerCase().includes(q)) ||
        (item.full && item.full.toLowerCase().includes(q))
      );
    });
  }, [currencies, activeTab, searchQuery]);

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Header
        toggleTheme={toggleTheme}
        language={language}
        toggleLanguage={toggleLanguage}
        t={t}
      />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3.5 }, mb: 4 }}>
        {/* Top Market Summary Banner */}
        <MarketOverview
          totalItems={currencies.length}
          time={time}
          isRefreshing={isRefreshing}
          t={t}
        />

        {/* Filter Controls & Search */}
        <ControlsBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onOpenConverter={() => handleOpenConverter('USD')}
          onRefresh={() => fetchCurrencyData(true)}
          isRefreshing={isRefreshing}
          t={t}
        />

        {/* Content Section */}
        {loading ? (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
                <CurrencySkeleton />
              </Grid>
            ))}
          </Grid>
        ) : filteredCurrencies.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              mt: 2,
            }}
          >
            <SearchOffRoundedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('noQuotesFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('noQuotesDesc', { query: searchQuery })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchQuery('');
                setActiveTab(0);
              }}
              sx={{
                borderRadius: 2.5,
                height: 36,
                px: 2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{t('clearFilters')}</span>
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {filteredCurrencies.map((item, index) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
                <CurrencyCard
                  {...item}
                  onSelect={() => handleOpenConverter(item.short)}
                  t={t}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Calculator Modal */}
      {currencies.length > 0 && (
        <ConverterModal
          open={converterOpen}
          onClose={() => setConverterOpen(false)}
          currencies={currencies}
          initialCurrency={selectedCurrency}
          t={t}
        />
      )}
    </Box>
  );
};

export default App;